import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useHasPermission from '@/hooks/useHasPermission';
import useSortState from '@/hooks/useSortState';
import {
  CompanyResource,
  DepartmentResource,
  FreelancerSubunitResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import CompanyTableRow from './components/CompaniesTableRow';
import CompanyFormModal from './components/CompanyFormModal';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: middle;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      width: 64px;
      padding-right: 24px;
    }
  }
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

// `/v1/companies` returns the company on its own, so the departments and
// subunits that belong to it are grouped here from their own lists rather than
// fetched per row. A department can belong to several companies, so it's listed
// under each of them.
const groupByCompany = (
  items: { id: number; name: string; company_ids: number[] }[],
) =>
  items.reduce<Record<number, { id: number; name: string }[]>>((acc, item) => {
    item.company_ids.forEach((companyId) => {
      acc[companyId] = [
        ...(acc[companyId] ?? []),
        { id: item.id, name: item.name },
      ];
    });

    return acc;
  }, {});

const Companies = () => {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  // `registration_code`, the invoice buyer details and `bank_account` only come
  // back for these users, so for anyone else the column would be a row of
  // dashes rather than "not set".
  const canSeeFinanceDetails = useHasPermission(
    'manage organisation settings',
    'manage invoices',
  );

  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'hide_from_dropdowns',
    direction: SortDirection.ASC,
  });

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: companies,
    isError,
    isLoading,
  } = useQuery(
    [
      'companies',
      {
        sort,
        search: debouncedSearch,
      },
    ],
    () =>
      api.get<never, CompanyResource[]>('/v1/companies', {
        params: {
          ...(!!search && {
            'filter[name]': debouncedSearch,
          }),
          ...sortQueryParamObj,
        },
      }),
    {
      staleTime: 90 * 1000,
    },
  );

  // Same query keys as useDepartmentsOptions / useFreelancerSubunitsOptions, so
  // these share one cache entry with the dropdowns instead of refetching.
  const { data: departments = [] } = useQuery(['departments_and_units'], () =>
    api.get<never, DepartmentResource[]>('/v1/departments'),
  );

  const { data: subunits = [] } = useQuery(['freelancer_subunits'], () =>
    api.get<never, FreelancerSubunitResource[]>('/v1/freelancer-subunits'),
  );

  const departmentsByCompany = useMemo(
    () =>
      groupByCompany(
        departments.map(({ id, name, companies }) => ({
          id,
          name,
          company_ids: companies.map((c) => c.id),
        })),
      ),
    [departments],
  );

  const subunitsByCompany = useMemo(
    () =>
      groupByCompany(
        subunits.map(({ id, name, company_id }) => ({
          id,
          name,
          company_ids: company_id === null ? [] : [company_id],
        })),
      ),
    [subunits],
  );

  return (
    <>
      <HeadingRow>
        <PageHeading>Companies</PageHeading>
        <Button onClick={() => setShowModal(true)}>Add New</Button>
      </HeadingRow>
      <SearchBar value={search} setValue={setSearch} />
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="name"
                title="Name"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="short"
                title="Short"
              />
              {canSeeFinanceDetails && <Th>Company Details</Th>}
              <Th>Departments</Th>
              <Th>Subunits</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="hide_from_dropdowns"
                title="Hide from dropdowns"
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!companies?.length}
              colSpan={canSeeFinanceDetails ? 7 : 6}
            >
              {companies?.map((company) => (
                <CompanyTableRow
                  key={company.id}
                  company={company}
                  departments={departmentsByCompany[company.id] ?? []}
                  subunits={subunitsByCompany[company.id] ?? []}
                  showFinanceDetails={canSeeFinanceDetails}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showModal && (
          <CompanyFormModal
            onClose={() => {
              setShowModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Companies;
